import { defineStore } from 'pinia';

/* eslint-disable import/no-unresolved */
import SendingWorker from '../../webWorkers/sendingWorker?worker';
import ResumableSendingWorker from '../../webWorkers/resumableSendingWorker?worker';
/* eslint-enable import/no-unresolved */

import { useAuthStore } from '@/stores';
import SenderService from '@/services/SenderService.js';
import { Logger } from '../../middlewares/sentry';
import { deepClone } from '../common';
import { useStateStore } from '../state.store';

const sendHistoryElement = {
  filesToSend: 0,
  sendedFiles: 0,
};

const MAX_RESPONSE_TIME = 30;

export const useSenderStore = defineStore('coreSender', {
  state: () => ({
    files: [],
    size: 0,
    totalSizeToSend: 0,
    sendedSize: 0,
    filesToSend: 0,
    sendedFiles: 0,
    sendHistory: [],
    isSending: false,
    sendedInfoShowed: true,

    slowConnectionTimerId: null,
    leftResponseTime: MAX_RESPONSE_TIME,
    isSlowConnetion: false,
  }),
  actions: {
    async sendImages(senderCreateService, senderService, clientId = '') {
      this.sendHistory.push(deepClone(sendHistoryElement));
      this.sendHistory[this.sendHistory.length - 1].filesToSend =
        this.numberOfFiles;

      const stateStore = useStateStore();
      if (clientId === '') {
        clientId = stateStore.clientId;
      } else {
        stateStore.getCommonData.isNewSender = false;
      }

      this.isSending = true;
      this.totalSizeToSend = this.size;

      this.leftResponseTime = MAX_RESPONSE_TIME;
      this.slowConnectionTimerId = setInterval(() => {
        if (this.leftResponseTime > 0) {
          Logger.info(`Left: ${this.leftResponseTime--} sec`);
        } else {
          clearInterval(this.slowConnectionTimerId);
          this.slowConnectionTimerId = null;
          this.isSlowConnetion = true;
        }
      }, 1000);

      if (window.Worker) {
        try {
          this.files.forEach((item, index) => {
            if (stateStore.getCommonData.isNewSender && item.sendingWorker) {
              const clonedItem = {
                file: item.file,
                purpose: item.purpose,
                tags: JSON.stringify(item.tags),
              };
              const { userToken } = useAuthStore();

              item.sendingWorker.postMessage({
                type: 'resume',
                item: clonedItem,
                clientId: clientId,
                token: userToken,
                senderService: senderService,
              });
            } else {
              let worker;
              if (stateStore.getCommonData.isNewSender) {
                worker = new ResumableSendingWorker();
              } else {
                worker = new SendingWorker();
              }

              const onWorkerError = (event) => {
                const error = event.data?.error ?? event.data;
                Logger.error(error);
                this.filesToSend--;
                if (this.sendedFiles === this.filesToSend) {
                  this.sendedFiles = 0;
                  this.filesToSend = 0;
                  this.isSending = false;
                  this.totalSizeToSend = 0;
                  this.sendedSize = 0;

                  clearInterval(this.slowConnectionTimerId);
                  this.slowConnectionTimerId = null;
                  this.isSlowConnetion = true;
                }
              };
              worker.onmessage = (event) => {
                const { type, fileName, size } = event.data;

                if (type === 'error') {
                  this.sendedSize += size;
                  if (this.sendedSize < 0) {
                    this.sendedSize = 0;
                  }
                  onWorkerError(event);
                } else if (type === 'progress') {
                  this.sendedSize += size;
                  if (this.sendedSize < 0) {
                    this.sendedSize = 0;
                  }

                  this.leftResponseTime = MAX_RESPONSE_TIME;
                } else if (type === 'success') {
                  this.files = this.files.filter((item) => {
                    return item.file.name !== fileName;
                  });
                  this.sendedFiles++;
                  this.size -= size;
                  if (this.sendedFiles === this.filesToSend) {
                    this.sendedFiles = 0;
                    this.filesToSend = 0;
                    this.isSending = false;
                    this.totalSizeToSend = 0;
                    this.sendedSize = 0;

                    clearInterval(this.slowConnectionTimerId);
                    this.slowConnectionTimerId = null;

                    this.sendHistory[this.sendHistory.length - 1].sendedFiles =
                      this.sendHistory[this.sendHistory.length - 1]
                        .filesToSend - this.numberOfFiles;
                  }
                }
              };
              worker.onerror = onWorkerError;

              const clonedItem = {
                file: item.file,
                purpose: item.purpose,
                tags: JSON.stringify(item.tags),
              };
              const { userToken } = useAuthStore();

              worker.postMessage({
                type: 'new',
                item: clonedItem,
                clientId: clientId,
                token: userToken,
                senderCreateService: senderCreateService,
                senderService: senderService,
              });
              this.files[index].sendingWorker = worker;
            }

            this.filesToSend++;
          });

          return true;
        } catch (err) {
          Logger.error(err);
          return false;
        }
      } else {
        Logger.warn('Worker not supported');

        const formData = new FormData();
        formData.append(`clientId`, clientId);

        this.files.forEach((_, index) => {
          formData.append(`purpose`, this.files[index].purpose);
          formData.append(`tags[]`, this.files[index].tags);
          formData.append(`file`, this.files[index].file);
        });
        this.files = [];

        try {
          await SenderService.runFunctionByName(senderService)(formData);
          this.isSending = false;
          this.size = 0;
          clearInterval(this.slowConnectionTimerId);
          this.slowConnectionTimerId = null;
          return true;
        } catch (err) {
          Logger.error(err);
          return false;
        }
      }
    },
    async retrySendImages(senderCreateService, senderService, clientId = '') {
      this.sendHistory.push(deepClone(sendHistoryElement));
      this.sendHistory[this.sendHistory.length - 1].filesToSend =
        this.numberOfFiles;

      const stateStore = useStateStore();
      if (clientId === '') {
        clientId = stateStore.clientId;
      }

      this.isSending = true;
      this.totalSizeToSend = this.size;
      if (window.Worker && stateStore.getCommonData.isNewSender) {
        try {
          this.files.forEach((item) => {
            if (item.sendingWorker) {
              const clonedItem = {
                file: item.file,
                purpose: item.purpose,
                tags: JSON.stringify(item.tags),
                startByte: 0,
              };
              const { userToken } = useAuthStore();

              item.sendingWorker.postMessage({
                type: 'resume',
                item: clonedItem,
                clientId: clientId,
                token: userToken,
                senderService: senderService,
              });
            }

            this.filesToSend++;
          });
          return true;
        } catch (err) {
          Logger.error(err);
          return false;
        }
      }
    },
    addImage(file, purpose, tags = []) {
      this.size += file.size;
      this.files.push({
        file: file,
        purpose: purpose,
        tags: tags,
        sendingWorker: undefined,
      });
    },
    updatePurpose(fileName, purpose) {
      this.files = this.files.map((item) => {
        if (item.file.name === fileName) {
          item.purpose = purpose;
        }
        return item;
      });
    },
    addTags(fileName, tags) {
      this.files = this.files.map((item) => {
        if (item.file.name === fileName) {
          item.tags = tags;
        }
        return item;
      });
    },
    removeImage(fileName) {
      this.files = this.files.filter((item) => {
        if (item.file.name === fileName) {
          this.size -= item.file.size;
        }
        return item.file.name !== fileName;
      });
    },
  },
  getters: {
    numberOfFiles: (state) => state.files.length,
    lastHistoryElement: (state) =>
      state.sendHistory[state.sendHistory.length - 1],
    isFailedFile: (state) => {
      return state.files.some((item) => {
        return item.sendingWorker !== undefined;
      });
    },
  },
});
